import { defineStore } from 'pinia'
import { documentRowStub } from '~~/shared/items/itemRow'
import type {
  CaseWire, CaseStatus, CasePriority, CaseDocument, CaseConversation, CaseSheet,
  CaseSuggestion, ClientCaseSuggestion,
} from '~~/shared/cases/case'
import type { CaseTask, TaskStatus } from '~~/shared/cases/caseTask'
import type { CaseNote, NotePriority } from '~~/shared/cases/caseNote'
import type { SheetRecord } from '~~/shared/sheets/sheet'
import type { BulkDeleteResult, SuccessResult } from '~~/shared/apiResults'
import type { CaseDocumentLink } from '~~/shared/cases/caseDocumentLink'
import type { CaseDeleteResult } from '~~/shared/clients/caseDelete'
import { createEntityCache, pruneIds } from '~/stores/entityCache'
import { useDocumentsStore } from '~/stores/documents'
import { useWebChatStore } from '~/stores/web-chat'
import { planAllows } from '~/composables/usePlanFeature'

/**
 * THE cases store (MAR-125). Cases = the hub inside a client; `client_id` is optional ("No Client").
 * See https://app.notion.com/p/3c304e9a592881b796eddb0748c5870a.
 *
 * Replaces `composables/useCases.ts`, `useCaseTasks.ts` and `useCaseNotes.ts` — all three stateless
 * API wrappers with no refs, so all fourteen consumers kept their own copy of the same cases and
 * refetched independently.
 *
 * **Tasks and notes live here rather than in stores of their own.** They are case-scoped children:
 * never fetched except for a case, never addressed except through one. A separate store would buy an
 * import and a second lifecycle to keep in step, and nothing else.
 *
 * **A case references its documents by id.** The linked-documents payload is a case-scoped VIEW —
 * `link_id`/`linked_at` are properties of the LINK, not of the document — so the document rows it
 * carries are seeded into the documents store and this holds the ids. That is the rule that keeps
 * "one place a document exists" true.
 */

/**
 * The case as the store holds it: the wire shape plus `documentIds`.
 *
 * The server sends membership as `document_ids` (snake_case, like every other column); `normalize`
 * remaps it, defaulting to `[]` rather than leaving it undefined — a group with undefined
 * membership and a group with none must not render differently. Both keys are present on a cached
 * record; `documentIds` is the one to read.
 */
export interface Case extends CaseWire {
  documentIds: string[]
}

// Re-exported: these names are imported from the store across the app. The DECLARATIONS live in
// `shared/cases/**`, which is what the services can be annotated with.
export type {
  CaseStatus, CasePriority, CaseDocument, CaseConversation, CaseSheet, CaseSuggestion, ClientCaseSuggestion,
  CaseTask, TaskStatus, CaseNote, NotePriority,
}

export interface CreateCaseInput {
  name: string
  clientId?: string | null
  status?: CaseStatus
  priority?: CasePriority | null
  dueDate?: string | null
  aiInstructions?: string
  color?: string | null
  emoji?: string | null
  linkItemIds?: string[]
}

// ── Tasks ───────────────────────────────────────────────────────────────────────────────────────
// `CaseTask` / `TaskStatus` are declared in `shared/cases/caseTask.ts` and re-exported above. What
// stays here is the REQUEST body, which is camelCase and belongs to no projection.
export interface CreateTaskInput {
  title: string
  label?: string | null
  assigneeId?: string | null
  dueDate?: string | null
  status?: TaskStatus
}

// ── Notes ───────────────────────────────────────────────────────────────────────────────────────
// `CaseNote` / `NotePriority` are declared in `shared/cases/caseNote.ts` and re-exported above.
export interface CreateNoteInput {
  title: string
  body?: string
  priority?: NotePriority | null
  conversationId?: string | null
}

export const useCasesStore = defineStore('cases', () => {
  const { apiFetch } = useApi()
  // Resolved at setup, never inside a getter — see the note in `stores/documents.ts`.
  const documents = useDocumentsStore()
  // Mirror `case_documents.item_id ON DELETE CASCADE`: the server has already dropped these links, so
  // a case must stop counting a document nobody can open. Subscribed once, at setup.
  documents.onRemoved(itemIds => forgetDocuments(itemIds))

  const cache = createEntityCache<Case>()
  /** The case list in server order — the view behind `/app/clients` (Casos tab). */
  const listIds = ref<string[]>([])
  const loading = ref(false)
  const loaded = ref(false)

  const list = computed<Case[]>(() => cache.resolve(listIds.value))

  /**
   * Wire row -> `Case`. The server sends membership as `document_ids` (snake_case, like every other
   * column); the store speaks `documentIds`. Defaulting to `[]` rather than leaving it undefined
   * matters: a group with undefined membership and a group with none must not render differently.
   */
  function normalize(raw: any): Case {
    return { ...raw, documentIds: raw.document_ids ?? raw.documentIds ?? [] }
  }

  function get(id: string): Case | null {
    return cache.get(id)
  }

  /** Cases belonging to one client, off the cache — no request, no second copy. */
  function forClient(clientId: string): Case[] {
    return cache.all().filter(c => c.client_id === clientId)
  }

  // ── Reads ─────────────────────────────────────────────────────────────────────────────────────
  /**
   * Fetch cases into the cache and return them.
   *
   * Returns the rows as well as caching them so one-shot callers keep working unchanged; anything
   * that wants to STAY current reads `list`. A filtered fetch caches its rows but does not become
   * the canonical list view.
   */
  async function listCases(filters: { q?: string; status?: string; clientId?: string } = {}): Promise<Case[]> {
    // A plan without case management never asks (MAR-980): `/api/cases` would answer a bare 403, and
    // the shell, the chat scope bar and the upload/source pickers all call this on pages every plan
    // reaches. The empty list is the true answer for such an org, so it is published as LOADED —
    // callers that re-ask only while `!loaded` stop asking.
    if (!(await planAllows('case_management'))) {
      if (!filters.q && !filters.status && !filters.clientId) {
        listIds.value = []
        loaded.value = true
      }
      return []
    }
    loading.value = true
    try {
      const rows = (await apiFetch<any[]>('/api/cases', { query: filters })).map(normalize)
      cache.upsert(rows)
      if (!filters.q && !filters.status && !filters.clientId) {
        listIds.value = rows.map(r => r.id)
        loaded.value = true
      }
      return rows
    } finally {
      loading.value = false
    }
  }

  /**
   * One case, with its linked documents.
   *
   * The document rows ride along on this payload; they are seeded into the documents store so the
   * case page and the sidebar agree about a document's name and status. `documents` stays on the
   * case record because its entries carry the LINK fields, which are the case's own.
   */
  async function getCase(id: string): Promise<Case> {
    const record = normalize(await apiFetch<any>(`/api/cases/${id}`))
    cache.upsert([record])
    seedDocuments(record.documents)
    return record
  }

  /**
   * Land a case's linked-document rows in the documents store.
   *
   * `upsert` merges, so a document already known in full is never narrowed by arriving through this
   * leaner case payload — which is exactly why the seeding is safe to do on every case read.
   */
  function seedDocuments(caseDocuments: CaseDocument[] | undefined) {
    if (!caseDocuments?.length) return
    documents.upsert(
      caseDocuments
        .filter(d => d.type === 'document')
        .map(d => documentRowStub({
          id: d.id,
          name: d.name,
          // The document's folder location inside THIS case — the wire carries it per link, and
          // dropping it landed every case-seeded document at the root `/` stub default.
          path: d.path,
          file_name: d.file_name,
          status: d.status,
          contract_type: d.contract_type,
          // The MAR-168 identity-slot eligibility flag, computed per link by `listCaseDocuments`.
          has_identity_data: d.has_identity_data,
        })),
    )
  }

  // ── Mutations ─────────────────────────────────────────────────────────────────────────────────
  /**
   * Create a case. The row lands in the cache immediately, which is the whole point: the document
   * sidebar derives its group headers from this store, so the new case appears there on the same
   * frame with nothing to invalidate and no refetch (MAR-125).
   */
  async function createCase(body: CreateCaseInput): Promise<Case> {
    const record = normalize(await apiFetch<any>('/api/cases', { method: 'POST', body }))
    cache.upsert([record])
    seedDocuments(record.documents)
    if (!listIds.value.includes(record.id)) listIds.value = [record.id, ...listIds.value]
    return record
  }

  async function updateCase(
    id: string,
    body: Partial<CreateCaseInput> & { status?: string; pinnedReminder?: string | null },
  ): Promise<Case> {
    // `upsert` merges, so the bare update row cannot blank `client_name` or `documentIds` — the
    // fields a PUT does not return are the ones the cached row already holds.
    const record = await apiFetch<any>(`/api/cases/${id}`, { method: 'PUT', body })
    cache.upsert([{ ...record, documentIds: record.document_ids ?? cache.get(id)?.documentIds ?? [] }])
    return cache.get(id)!
  }

  async function deleteCase(id: string) {
    const res = await apiFetch<SuccessResult & CaseDeleteResult>(`/api/cases/${id}`, { method: 'DELETE' })
    forget([id])
    mirrorUnattached(res)
    await unscopeChat([id])
    return res
  }

  /** Atomic multi-delete — one statement server-side, so a partial batch can't happen. */
  async function deleteCases(ids: string[]) {
    const res = await apiFetch<BulkDeleteResult & CaseDeleteResult>(
      '/api/case-bulk-delete', { method: 'POST', body: { ids } },
    )
    const deleted = res.deletedIds ?? ids
    forget(deleted)
    mirrorUnattached(res)
    await unscopeChat(deleted)
    return res
  }

  /**
   * A deleted case's documents that nothing else held are now attached to NOTHING (MAR-1006) — the
   * server names them in the delete's response. Mark them so "Agrupar por: Clientes" shows them in its
   * virtual "Sin cliente" group on the same frame, rather than in no client group until a reload.
   */
  function mirrorUnattached(res: Partial<CaseDeleteResult> | undefined) {
    for (const itemId of res?.unattachedItemIds ?? []) documents.markUnattached(itemId)
  }

  /**
   * Deleting a case the chat is scoped to must clear the chip (MAR-461) — see
   * `clearScopeForDeleted`. It hangs off the delete calls rather than off `forget`, which is also a
   * cache eviction and must stay one: forgetting a row is not a claim that it no longer exists.
   *
   * The store is resolved here, not in this store's setup, so the chat store is not instantiated by
   * every page that merely lists cases.
   */
  const unscopeChat = (ids: string[]) => useWebChatStore().clearScopeForDeleted(ids)

  /**
   * Drop cases from the cache, and return any documents they held to the Unfiled bucket.
   *
   * The second half is easy to forget and was: deleting a case made its group disappear but left its
   * documents belonging to nothing — absent from every group AND absent from Unfiled, so they simply
   * vanished from the grouped sidebar until a reload. Found by browser testing, not by the unit test
   * for this path, which only asserted the group was gone and never asked where its documents went.
   *
   * Membership is collected BEFORE the removal and re-checked AFTER it, so a document still held by
   * another surviving case is not wrongly marked unfiled.
   */
  function forget(ids: string[]) {
    const held = new Set<string>()
    for (const id of ids) {
      for (const itemId of cache.get(id)?.documentIds ?? []) held.add(itemId)
    }

    cache.remove(ids)
    listIds.value = pruneIds(listIds.value, new Set(ids))

    for (const itemId of held) {
      if (!cache.all().some(c => c.documentIds.includes(itemId))) documents.markUngrouped(itemId)
    }
  }

  // ── Linked documents ──────────────────────────────────────────────────────────────────────────
  /**
   * Link a document into a case. `path` is its folder location INSIDE the case
   * (`case_documents.path`); defaults to root.
   *
   * Membership is maintained locally rather than refetched. That is sound because this is one of only
   * two client paths that can change `case_documents` at all — the server has exactly one INSERT and
   * one DELETE site, both behind these two calls, and nothing writes `link_source: 'auto'`.
   */
  async function addCaseDocument(id: string, itemId: string, path = '/') {
    const res = await apiFetch<CaseDocumentLink | undefined>(`/api/cases/${id}/documents`, { method: 'POST', body: { itemId, path } })
    link(id, itemId)
    return res
  }

  /** Move a document to another folder within this case (does not touch its library location). */
  const moveCaseDocument = (id: string, itemId: string, path: string) =>
    apiFetch<SuccessResult>(`/api/cases/${id}/documents/${itemId}`, { method: 'PUT', body: { path } })

  async function removeCaseDocument(id: string, itemId: string) {
    const res = await apiFetch<SuccessResult>(`/api/cases/${id}/documents/${itemId}`, { method: 'DELETE' })
    unlink(id, itemId)
    return res
  }

  /** Record a link locally. Idempotent — the server's insert is `ON CONFLICT DO NOTHING`. */
  function link(caseId: string, itemId: string) {
    const record = cache.get(caseId)
    if (!record || record.documentIds.includes(itemId)) return
    cache.patch(caseId, { documentIds: [...record.documentIds, itemId] })
    // It now belongs to a case, so it is out of the "no case" bucket and attached to something.
    documents.unmarkUngrouped(itemId)
  }

  function unlink(caseId: string, itemId: string) {
    const record = cache.get(caseId)
    if (!record || !record.documentIds.includes(itemId)) return
    cache.patch(caseId, { documentIds: record.documentIds.filter(d => d !== itemId) })
    // That bucket means linked to NO case — check the others before sending it back to it. Whether
    // the document is also attached to NOTHING is a client/conversation question `fetchGroups` owns.
    if (!cache.all().some(c => c.documentIds.includes(itemId))) documents.markUngrouped(itemId)
  }

  /**
   * A document ceased to exist — drop it from every case that linked it.
   *
   * `case_documents.item_id` is `ON DELETE CASCADE`, so the server has already done this; the client
   * mirrors it so the sidebar does not keep rendering a group that counts a document nobody can open.
   * Called by the documents store's `remove`.
   */
  function forgetDocuments(itemIds: string[]) {
    if (itemIds.length === 0) return
    const gone = new Set(itemIds)
    const touched = cache.all().filter(c => c.documentIds.some(id => gone.has(id)))
    if (touched.length === 0) return
    cache.upsert(touched.map(c => ({ ...c, documentIds: c.documentIds.filter(id => !gone.has(id)) })))
  }

  // ── Suggestions / related surfaces ────────────────────────────────────────────────────────────
  const listCaseSuggestions = (clientId: string) =>
    apiFetch<CaseSuggestion[]>(`/api/clients/${clientId}/document-suggestions`)

  /** Suggested cases across all existing clients — powers the Casos-view ghost cards. */
  const listAllCaseSuggestions = () =>
    apiFetch<ClientCaseSuggestion[]>('/api/case-suggestions')

  const listCaseConversations = (id: string) =>
    apiFetch<CaseConversation[]>(`/api/cases/${id}/conversations`)

  const listCaseSheets = (id: string) =>
    apiFetch<CaseSheet[]>(`/api/cases/${id}/sheets`)

  /**
   * Create a Multi-Doc Analysis sheet scoped to this case, pre-seeded with the given documents
   * (the case's linked docs). The sheet is hard-scoped server-side to the case (§18).
   */
  const createCaseSheet = (id: string, body: { name: string; itemIds?: string[] }) =>
    apiFetch<SheetRecord>('/api/sheets', {
      method: 'POST',
      body: { name: body.name, caseId: id, itemIds: body.itemIds ?? [] },
    })

  // ── Tasks (case-scoped children) ──────────────────────────────────────────────────────────────
  const listTasks = (caseId: string) =>
    apiFetch<CaseTask[]>(`/api/cases/${caseId}/tasks`)

  const createTask = (caseId: string, body: CreateTaskInput) =>
    apiFetch<CaseTask>(`/api/cases/${caseId}/tasks`, { method: 'POST', body })

  const updateTask = (caseId: string, taskId: string, body: Partial<CreateTaskInput>) =>
    apiFetch<CaseTask>(`/api/cases/${caseId}/tasks/${taskId}`, { method: 'PUT', body })

  const deleteTask = (caseId: string, taskId: string) =>
    apiFetch<SuccessResult>(`/api/cases/${caseId}/tasks/${taskId}`, { method: 'DELETE' })

  // ── Notes (case-scoped children; body is TipTap HTML) ─────────────────────────────────────────
  const listNotes = (caseId: string) =>
    apiFetch<CaseNote[]>(`/api/cases/${caseId}/notes`)

  const createNote = (caseId: string, body: CreateNoteInput) =>
    apiFetch<CaseNote>(`/api/cases/${caseId}/notes`, { method: 'POST', body })

  const deleteNote = (caseId: string, noteId: string) =>
    apiFetch<SuccessResult>(`/api/cases/${caseId}/notes/${noteId}`, { method: 'DELETE' })

  return {
    byId: cache.byId, list, loading, loaded, get, forClient, forget,
    listCases, getCase, createCase, updateCase, deleteCase, deleteCases,
    addCaseDocument, moveCaseDocument, removeCaseDocument, link, unlink, forgetDocuments,
    listCaseSuggestions, listAllCaseSuggestions, listCaseConversations, listCaseSheets, createCaseSheet,
    listTasks, createTask, updateTask, deleteTask,
    listNotes, createNote, deleteNote,
  }
})
